import 'styled-components';
import type { DefaultTheme } from 'styled-components';

export type ColorMode = 'dark' | 'light';

const baseTheme = {
  fonts: {
    heading: "'Clash Display', -apple-system, BlinkMacSystemFont, sans-serif",
    body: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    display: "'Clash Display', -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'JetBrains Mono', monospace",
  },
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
    xxxl: '64px',
    section: '120px',
  },
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
  },
  maxWidth: '1400px',
  borderRadius: '0',
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  },
  transitions: {
    fast: '0.15s cubic-bezier(0.25, 1, 0.5, 1)',
    medium: '0.3s cubic-bezier(0.25, 1, 0.5, 1)',
    slow: '0.5s cubic-bezier(0.25, 1, 0.5, 1)',
  },
};

export const themes: Record<string, Record<ColorMode, DefaultTheme>> = {
  green: {
    dark: {
      ...baseTheme,
      name: 'neon-matrix',
      colors: {
        primary: '#00ff88',
        background: 'oklch(18% 0.010 150)',
        surface: 'oklch(22% 0.010 150)',
        text: 'oklch(90% 0.010 150)', // Softer than pure white
        textSecondary: 'oklch(70% 0.010 150)',
        border: 'oklch(28% 0.010 150)',
        card: 'oklch(22% 0.010 150)',
      },
    },
    light: {
      ...baseTheme,
      name: 'neon-matrix',
      colors: {
        primary: '#009951',
        background: 'oklch(96% 0.010 150)', // Soft cream/slate, prevents glare
        surface: 'oklch(99% 0.005 150)', // Brighter surface for cards
        text: 'oklch(25% 0.010 150)', // Soft charcoal, prevents astigmatism strain
        textSecondary: 'oklch(45% 0.010 150)',
        border: 'oklch(90% 0.010 150)',
        card: 'oklch(99% 0.005 150)',
      },
    },
  },
  blue: {
    dark: {
      ...baseTheme,
      name: 'cyber-blue',
      colors: {
        primary: '#00e5ff',
        background: 'oklch(18% 0.010 250)',
        surface: 'oklch(22% 0.010 250)',
        text: 'oklch(90% 0.010 250)',
        textSecondary: 'oklch(70% 0.010 250)',
        border: 'oklch(28% 0.010 250)',
        card: 'oklch(22% 0.010 250)',
      },
    },
    light: {
      ...baseTheme,
      name: 'cyber-blue',
      colors: {
        primary: '#008ba3',
        background: 'oklch(96% 0.010 250)',
        surface: 'oklch(99% 0.005 250)',
        text: 'oklch(25% 0.010 250)',
        textSecondary: 'oklch(45% 0.010 250)',
        border: 'oklch(90% 0.010 250)',
        card: 'oklch(99% 0.005 250)',
      },
    },
  },
  orange: {
    dark: {
      ...baseTheme,
      name: 'sunset-orange',
      colors: {
        primary: '#ff4d00',
        background: 'oklch(18% 0.010 40)',
        surface: 'oklch(22% 0.010 40)',
        text: 'oklch(90% 0.010 40)',
        textSecondary: 'oklch(70% 0.010 40)',
        border: 'oklch(28% 0.010 40)',
        card: 'oklch(22% 0.010 40)',
      },
    },
    light: {
      ...baseTheme,
      name: 'sunset-orange',
      colors: {
        primary: '#c93c00',
        background: 'oklch(96% 0.010 40)',
        surface: 'oklch(99% 0.005 40)',
        text: 'oklch(25% 0.010 40)',
        textSecondary: 'oklch(45% 0.010 40)',
        border: 'oklch(90% 0.010 40)',
        card: 'oklch(99% 0.005 40)',
      },
    },
  },
  pink: {
    dark: {
      ...baseTheme,
      name: 'synthwave-pink',
      colors: {
        primary: '#ff00ea',
        background: 'oklch(18% 0.010 320)',
        surface: 'oklch(22% 0.010 320)',
        text: 'oklch(90% 0.010 320)',
        textSecondary: 'oklch(70% 0.010 320)',
        border: 'oklch(28% 0.010 320)',
        card: 'oklch(22% 0.010 320)',
      },
    },
    light: {
      ...baseTheme,
      name: 'synthwave-pink',
      colors: {
        primary: '#bf00af',
        background: 'oklch(96% 0.010 320)',
        surface: 'oklch(99% 0.005 320)',
        text: 'oklch(25% 0.010 320)',
        textSecondary: 'oklch(45% 0.010 320)',
        border: 'oklch(90% 0.010 320)',
        card: 'oklch(99% 0.005 320)',
      },
    },
  },
};

export const theme = themes.green.dark;
export default theme;
